import { useState, useMemo } from 'react';
import DataTable from '../common/DataTable/index';
import AddAmc from '../AMC/AddAmc';
import CommonDialog from '../common/CommonDialog';
import { Button, Box, Autocomplete, TextField } from '@mui/material';
import { FilterFieldSx } from '../../../constants/style';
import { userScreenAccess } from '../../../utils';
import { amcTypeOptions } from '../../../constants/constant';

const headers = [
  {
    label: 'Is Temprorary',
    key: 'isTemporary',
    minWidth: 120,
    align: 'center',
    sortable: true,
    render: (val) => <span>{val ? 'Yes' : 'No'}</span>
  },
  {
    label: 'Start Date',
    key: 'amcStartDate',
    minWidth: 120,
    sortable: true,
    resizable: true,
    render: (val) => {
      const result = val.slice(0, 10);
      return <span>{result}</span>;
    }
  },
  {
    label: 'End Date',
    key: 'amcEndDate',
    minWidth: 120,
    sortable: true,
    resizable: true,
    render: (val) => {
      const result = val.slice(0, 10);
      return <span>{result}</span>;
    }
  },
  {
    label: 'Bill No.',
    key: 'billNo',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Bill Date',
    key: 'paidDate',
    minWidth: 120,
    sortable: true,
    resizable: true,
    render: (val) => {
      const result = val ? val.slice(0, 10) : null;
      return <span>{result}</span>;
    }
  },
  {
    label: 'Bill Amount',
    key: 'paidAmount',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Days',
    key: 'days',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Remarks',
    key: 'remarks',
    minWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Actions',
    key: '_actions',
    minWidth: 80,
    sortable: false,
    align: 'center',
    render: (_val, row) => (
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          _editRowHandler?.(row);
        }}
        sx={{
          fontSize: '0.7rem',
          textTransform: 'none',
          borderRadius: 1.5,
          py: 0.3,
          px: 1.2,
          borderColor: 'divider',
          color: 'text.secondary',
          '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
        }}
      >
        Edit
      </Button>
    )
  }
];

let _editRowHandler = null;

const AmcList = ({ isUser = false, installationGUID = 0, amcList = [], onRefresh }) => {
  const [openAddAmcDialog, setOpenAddAmcDialog] = useState(false);
  const [selectedAMC, setSelectedAMC] = useState(null);
  const [selectedAmcType, setSelectedAmcType] = useState(amcTypeOptions[0]);
  const tabelHeader = isUser ? headers.filter((header) => header.key !== '_actions') : headers;
  const userAccess = userScreenAccess('/amc-list');

  // Filter AMC list based on selected AMC Type
  const filteredAmcList = useMemo(() => {
    if (!selectedAmcType || selectedAmcType.value === 'all') {
      return amcList;
    }
    if (selectedAmcType.value === 'temp') {
      return amcList.filter((item) => item.isTemporary === true);
    }
    if (selectedAmcType.value === 'annual') {
      return amcList.filter((item) => item.isTemporary === false);
    }
    return amcList;
  }, [amcList, selectedAmcType]);

  // Wire module-level handler for the Actions column
  _editRowHandler = (row) => {
    if (!userAccess.hasEditPermission) return;
    setSelectedAMC(row);
    setOpenAddAmcDialog(true);
  };

  const handleCloseAddAmcDialog = () => {
    setOpenAddAmcDialog(false);
    setSelectedAMC(null);
  };

  const handleAmcTypeChange = (event, value) => {
    setSelectedAmcType(value || amcTypeOptions[0]);
  };

  return (
    <>
      <div className="w-100 mt-2">
        <Box sx={{ mb: 2 }}>
          <Autocomplete
            size="small"
            options={amcTypeOptions}
            value={selectedAmcType}
            onChange={handleAmcTypeChange}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            disablePortal={false}
            sx={{ width: { xs: '100%', sm: '250px' } }}
            slotProps={{
              paper: {
                sx: { width: '100%', minWidth: '250px' }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="AMC Type"
                placeholder="Select AMC Type"
                sx={FilterFieldSx}
              />
            )}
          />
        </Box>
        <DataTable
          headers={tabelHeader}
          data={filteredAmcList}
          selectable={false}
          emptyMessage={'No AMC details found in the system.'}
          enablePagination={true}
          zebra={false}
          onRowClick={_editRowHandler}
        />
      </div>
      <CommonDialog
        open={openAddAmcDialog}
        onClose={handleCloseAddAmcDialog}
        title={`Edit ${selectedAMC?.isTemporary ? 'Temporary' : 'Annual'} AMC`}
        persistent={true}
      >
        <AddAmc
          installationId={installationGUID}
          amcDetails={selectedAMC}
          userAccess={userAccess}
          amcEndDate={!selectedAMC?.isTemporary ? selectedAMC?.amcEndDate : null}
          tempAmcEndDate={selectedAMC?.isTemporary ? selectedAMC?.amcEndDate : null}
          onSuccess={() => {
            handleCloseAddAmcDialog();
            onRefresh();
          }}
        />
      </CommonDialog>
    </>
  );
};

export default AmcList;
